import { useMemo } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth, AUTH_ENABLED } from '../lib/auth';
import { useProfileDetail } from '../hooks/useProfiles';
import { useUserSettings } from '../hooks/useUserSettings';
import { useProperties } from '../hooks/useProperties';
import { useProfileOffers } from '../hooks/useProfileOffers';
import { OfferStatusBadge } from '../components/ui/OfferStatusBadge';
import { fmtDollarInt } from '../lib/format';
import { getDefaultInputs, calculateAll } from '../lib/calculator';
import { getPaymentIndicator, getCashIndicator } from '../lib/price-levels';
import type { Property, Offer } from '../lib/types';
import { ProfilePageSkeleton } from '../components/ui/Skeletons';

function DashboardContent() {
  const { getToken } = useAuth();
  const { defaultProfileId, settingsLoading } = useUserSettings(getToken);
  const profileId = defaultProfileId;
  const { profile, loading: profileLoading } = useProfileDetail(profileId ?? undefined, getToken);
  const { properties, loading: propsLoading } = useProperties(profileId ?? undefined, getToken);
  const { offers, loading: offersLoading } = useProfileOffers(profileId, getToken);
  const navigate = useNavigate();

  // Quick affordability estimate per property
  const propertyAffordability = useMemo(() => {
    type AffData = {
      monthlyPayment: number;
      cashToClose: number;
      payIndicator: 'green' | 'yellow' | 'red';
      cashIndicator: 'green' | 'yellow' | 'red';
      score: number;
    };
    const map = new Map<string, AffData>();
    if (!profile || properties.length === 0) return map;

    const downPct = profile.defaultDownPaymentPct ?? 10;
    const combinedIncome = profile.grossAnnualIncome + (profile.coBorrower?.grossAnnualIncome ?? 0);
    const combinedDebts = profile.monthlyDebts + (profile.coBorrower?.monthlyDebts ?? 0);
    const grossMonthly = combinedIncome / 12;
    const indScore = (ind: 'green' | 'yellow' | 'red') => ind === 'green' ? 2 : ind === 'yellow' ? 1 : 0;

    for (const prop of properties) {
      const inputs = {
        ...getDefaultInputs(),
        homePrice: prop.askingPrice,
        downPaymentPct: downPct,
        downPaymentAmt: Math.round(prop.askingPrice * (downPct / 100)),
        creditScore: profile.creditScoreRange,
        grossAnnualIncome: combinedIncome,
        monthlyOtherDebt: combinedDebts,
        monthlyCarPayment: 0,
        monthlyStudentLoans: 0,
        monthlyCreditCards: 0,
      };
      const res = calculateAll(inputs);
      const monthlyPayment = res.totalMonthly;
      const cashToClose = res.cashToClose.cashToClose;
      const payIndicator = getPaymentIndicator(monthlyPayment, grossMonthly);
      const cashIndicator = getCashIndicator(cashToClose, profile.cashOnHand);
      map.set(prop.id, {
        monthlyPayment,
        cashToClose,
        payIndicator,
        cashIndicator,
        score: Math.min(indScore(payIndicator), indScore(cashIndicator)),
      });
    }
    return map;
  }, [properties, profile]);

  // Build a map of propertyId → property for offer display
  const propertyMap = useMemo(() => {
    const map = new Map<string, Property>();
    for (const p of properties) map.set(p.id, p);
    return map;
  }, [properties]);

  // Only show active offers (non-withdrawn, non-rejected)
  const activeOffers = useMemo(() =>
    offers.filter(o => o.status !== 'withdrawn' && o.status !== 'rejected'),
  [offers]);

  const loading = profileLoading || propsLoading || settingsLoading;

  // No default profile set → redirect to profile list
  if (!settingsLoading && !defaultProfileId) {
    return <Navigate to="/profiles" replace />;
  }

  if (loading) return <ProfilePageSkeleton />;

  if (!profile) {
    return (
      <div className="page-container">
        <h2 className="page-title">Dashboard</h2>
        <div className="note-box" style={{ borderColor: 'var(--red)', background: 'rgba(239,68,68,0.1)' }}>
          Default profile not found. It may have been deleted.
        </div>
        <Link to="/profiles" className="btn btn-secondary" style={{ marginTop: '1rem', display: 'inline-block' }}>Manage Profiles</Link>
      </div>
    );
  }

  const co = profile.coBorrower;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>{profile.name}</h2>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Default profile</span>
        </div>
        <Link to="/profiles" className="btn btn-secondary btn-sm">Manage Profiles</Link>
      </div>

      {/* Financial Summary */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Financial Summary</span>
          <Link to={`/profiles/${profile.id}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>View Profile</Link>
        </div>
        <div className="card-body">
          <div className="financial-summary-grid">
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--primary)' }}>{fmtDollarInt(profile.cashOnHand)}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Cash on Hand</div>
            </div>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                {profile.defaultDownPaymentPct ?? 10}%
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Default Down Payment</div>
            </div>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                {fmtDollarInt(profile.grossAnnualIncome + (co?.grossAnnualIncome ?? 0))}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {co ? 'Combined Annual Income' : 'Annual Income'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                {fmtDollarInt(profile.monthlyDebts + (co?.monthlyDebts ?? 0))}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {co ? 'Combined Monthly Debts' : 'Monthly Debts'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                {co
                  ? `${Math.min(parseInt(profile.creditScoreRange), parseInt(co.creditScoreRange))}+`
                  : `${profile.creditScoreRange}+`}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {co ? 'Qualifying Credit Score' : 'Credit Score'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Offers */}
      {!offersLoading && activeOffers.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1.5rem 0 0.75rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Active Offers</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {activeOffers.map(offer => {
              const prop = propertyMap.get(offer.propertyId);
              return (
                <div
                  key={offer.id}
                  className="card"
                  style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                  onClick={() => navigate(`/profiles/${profile.id}/properties/${offer.propertyId}/offers/${offer.id}`)}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
                >
                  <div className="card-body" style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <div style={{ flex: '1 1 0', minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          {prop?.address || 'Unknown property'}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                          {prop ? [prop.city, prop.county ? `${prop.county} County` : ''].filter(Boolean).join(', ') : ''}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '1rem' }}>{fmtDollarInt(offer.offerPrice)}</div>
                        <div style={{ marginTop: '4px' }}>
                          <OfferStatusBadge status={offer.status} />
                        </div>
                      </div>
                      <span style={{ color: 'var(--text-muted)', fontSize: '1.1rem', lineHeight: 1, alignSelf: 'center', flexShrink: 0 }}>&rsaquo;</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Saved Properties */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1.5rem 0 0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Saved Properties</h3>
      </div>

      {properties.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏡</div>
          <div className="empty-state-text">No properties saved yet</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Add a property from your profile to start analyzing offer prices.
          </p>
          <Link to={`/profiles/${profile.id}`} className="btn btn-primary" style={{ textDecoration: 'none' }}>
            Go to Profile
          </Link>
        </div>
      ) : (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {properties.filter(p => !p.archived).map(prop => {
            const aff = propertyAffordability.get(prop.id);
            const worstInd = aff ? (['red', 'yellow', 'green'] as const)[aff.score] : undefined;
            return (
              <div
                key={prop.id}
                className="card"
                style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onClick={() => navigate(`/profiles/${profile.id}/properties/${prop.id}`)}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
              >
                <div className="card-body" style={{ padding: '0.75rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    {worstInd && (
                      <span
                        className={`indicator indicator-${worstInd}`}
                        style={{ marginTop: '0.3rem', flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{prop.address}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {[prop.city, prop.county ? `${prop.county} County` : ''].filter(Boolean).join(', ')}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem' }}>{fmtDollarInt(prop.askingPrice)}</div>
                      {aff && (
                        <>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                            <span className={`indicator indicator-${aff.payIndicator}`} />
                            {' '}~{fmtDollarInt(aff.monthlyPayment)}/mo
                          </div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            <span className={`indicator indicator-${aff.cashIndicator}`} />
                            {' '}~{fmtDollarInt(aff.cashToClose)} cash
                          </div>
                        </>
                      )}
                    </div>
                    <span style={{ color: 'var(--text-muted)', fontSize: '1.1rem', lineHeight: 1, alignSelf: 'center', flexShrink: 0 }}>&rsaquo;</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {profile && properties.filter(p => !p.archived).length > 0 && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>Estimates based on profile defaults</span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <span><span className="indicator indicator-green" /> Affordable</span>
              <span><span className="indicator indicator-yellow" /> Tight</span>
              <span><span className="indicator indicator-red" /> Over budget</span>
            </span>
          </div>
        )}
        </>
      )}
    </div>
  );
}

export function DashboardPage() {
  if (!AUTH_ENABLED) {
    return (
      <div className="page-container">
        <h2 className="page-title">Dashboard</h2>
        <div className="note-box">
          Sign in to create buyer profiles, save properties, and track offers.
          Configure <code>VITE_CLERK_PUBLISHABLE_KEY</code> to enable authentication.
        </div>
      </div>
    );
  }

  return <DashboardContent />;
}
